import React, { useState, useEffect, useRef, useMemo } from "react"
import List from './list'
import Filters from './filters'
import Selector from './selector'
import Advert from 'shared/advert.jsx'
import EnquiryForm from './enquiry-form'
import axios from 'axios'
import 'loader-blocks.gif';

const Grid = ({ config, filters, results }) => {

	// list = the same cards as the grid plus the activity blurb, and it is the
	// default everywhere; the "Show activity details" toggle switches to grid,
	// which is the identical card without the text. No window here — prod
	// prerenders this component server-side.
	const [ layout, setLayout ] = useState('list')
	// Separate from layout: layout is card density (grid/list), the map is a panel
	// above them. Google bills per dynamic map load, so it stays unmounted — and
	// the Maps JS unfetched — until the guest asks for it.
	const [ showMap, setShowMap ] = useState( false )
	// The activities the concierge measured for a "how far apart" question — the one
	// thing that lights up on both the map and the cards. Nothing else crosses
	// between them: the map and the list are independent.
	const [ compared, setCompared ] = useState( [] )
	const [ type, setType ] = useState( config.type )
	const [ loading, setLoading ] = useState( false )
	const [ searchResults, setSearchResults ] = useState( results )
	
	let originalPriceRange = false
	if( results.price != undefined ) {
		originalPriceRange = results.price
	}
	
	// A selection is one of three shapes: an array of {id,name} for a multi
	// filter, {id,name} for a single one, or {min,max} for price. Reading .id
	// off an array or a price range yields undefined, which silently dropped a
	// pre-selected facet from the payload the moment anything else refetched
	// (changing the sort order, for instance).
	const selectionToFilterValue = ( selection ) => {
		if( Array.isArray( selection ) ) {
			return selection.map( item => item.id )
		}
		return selection.id !== undefined ? selection.id : selection
	}

	// Filter definitions are state so the concierge can pre-select facets after
	// load; Filters seeds its own state from default_selection, so it is
	// remounted via filtersKey when they change.
	const [ filterDefinitions, setFilterDefinitions ] = useState( filters )
	const [ filtersKey, setFiltersKey ] = useState( 0 )

	let defaultFilters = {}
	{Object.keys(filters).map(( filterKey ) => {
		const filter = filters[filterKey]
		if( filter.default_selection !== undefined && filter.default_selection !== null ) {
			defaultFilters[filterKey] = selectionToFilterValue( filter.default_selection )
		}
	})}

	
	const [ payload, setPayload ] = useState({
		 type: config.type,
		 path: config.path,
		 order: 1,
		 filters: defaultFilters
	})
		
	const mounted = React.useRef(false);
			
		
	useEffect(() => {

		if( mounted.current ) {
			const unmount = loadData();
			return () => unmount && unmount();
		} else {
			mounted.current = true;
		}
		
	}, [payload]);

	const changeOrder = ( order ) => {
		if( order !== parseInt( searchResults.order )) {
			setPayload( previous => ({ ...previous, order: order }) )
		}
	}
	
	const runFilters = ( sentFilters ) => {
				
		const filterValues = {}
		const tags = []

		Object.keys(sentFilters).forEach( filter => {

			const definition = filterDefinitions[filter]
			
			if( definition.ident == 'price' ) {
				filterValues[definition.ident] = sentFilters[filter] !== undefined ? sentFilters[filter] : null
			} else {
				 if( definition.config.multi ) {
				 	 const values = []
				 	 if( sentFilters[filter] !== undefined || sentFilters[filter] !== null ) {
				 	 	 sentFilters[filter].forEach( item => {
				 	 	 	if( !item.id !== undefined ) {
				 	 	 		values.push( item.id )
				 	 	 	}
				 	 	 })
				 	 	 filterValues[definition.ident] = values.length > 0 ? values : null
				 	 } else {
				 	 	 filterValues[definition.ident] = null
				 	 }
				 } else {
				 	 filterValues[definition.ident] = sentFilters[filter] !== null && sentFilters[filter].id !== undefined  ? sentFilters[filter].id : null
				 }
			}
			
			if( definition.config.tag && filterValues[definition.ident] !== null ) {
				if( definition.config.multi ) {
					//TODO: multi definition for tags
				} else {
					tags.push(filterValues[definition.ident])
				}
			}
			
		})
		
		//if( tags.length ) {
		filterValues['tags'] = tags

		// filterValues is rebuilt from the sidebar's own definitions, and `near`
		// isn't one of them — carried over explicitly so touching a filter doesn't
		// silently drop the neighbourhood the guest asked for.
		setPayload( previous => ({
			...previous,
			filters: previous.filters.near ? { ...filterValues, near: previous.filters.near } : filterValues
		}) )
	}

	const switchLayout = ( e, layout ) => {
		e.preventDefault();
		e.stopPropagation();
		setLayout(layout);
	}
	
	const loadData = () => {
		setLoading( true );

		axios.post( '/products/list2', {
			authenticity_token: config.token,
			search: payload,
			// list2 writes this back to session[:enquiry] when present; when
			// absent it searches with whatever the session already holds.
			enquiry: payload.enquiry
		}).then( response => {
			setLoading( false );
			setSearchResults( response.data );

		}).catch(err => {
			setLoading( false );
			console.log(err);
		});
	}

	// The concierge owns every facet except section and location, which are
	// fixed by the URL (it navigates to change those). This in-place refine is
	// the only place facets are applied — the server no longer seeds them from
	// the session on page load.
	const conciergeManages = ( ident ) => ident !== 'section' && ident !== 'location'

	// Group size and date from the concierge pills. They live in
	// session[:enquiry] server-side, but list2 accepts them and writes the
	// session itself, so a pill edit refines in place instead of the form
	// POSTing a full page load that dumps the guest back at the top.
	const applyEnquiry = ( enquiry ) => {
		setPayload( previous => ({ ...previous, enquiry: enquiry }) )
	}

	// A "how far apart are these" answer is only readable on a map, so open it.
	// "Near somewhere" also narrows the results to that area, which is a refetch —
	// the filter travels in the payload rather than as a facet, because it is a
	// point and a radius rather than one of the page's own filter definitions.
	// Updater form, not { ...payload }: this and applyConciergeFacets both run in
	// the same tick after a concierge turn, and read the same render's payload. The
	// object form meant whichever ran second overwrote the first — near was set and
	// then immediately dropped, before any request went out.
	const showProximity = ( compare, near ) => {
		setCompared( compare )
		setShowMap( true )
		if( near ) {
			setPayload( previous => ({ ...previous, filters: { ...previous.filters, near: near } }) )
		}
	}

	const clearNear = () => {
		setPayload( previous => {
			const filters = Object.assign( {}, previous.filters )
			delete filters.near
			return { ...previous, filters: filters }
		})
	}

	// Absent from facets means the guest dropped it, so clear rather than keep —
	// otherwise a removal in conversation never takes effect.
	const applyConciergeFacets = ( facets ) => {

		const definitions = {}
		const patch = {}

		Object.keys( filterDefinitions ).forEach( key => {

			const definition = filterDefinitions[key]

			if( !conciergeManages( definition.ident ) ) {
				definitions[key] = definition
				return
			}

			const selection = facets[ definition.ident ]
			definitions[key] = Object.assign( {}, definition, {
				default_selection: selection !== undefined ? selection : null
			})
			patch[ definition.ident ] = selection !== undefined ? selectionToFilterValue( selection ) : null
		})

		setFilterDefinitions( definitions )
		setFiltersKey( filtersKey + 1 )   // Filters seeds state on mount only
		// Merged into whatever the payload holds by then rather than into this
		// render's copy, so a `near` set moments earlier in the same tick survives.
		setPayload( previous => ({ ...previous, filters: { ...previous.filters, ...patch } }) )
	}

    return (
      <React.Fragment>

		{config.selector &&
			<Selector selector={config.selector} />
		}

		{/* The concierge lives inside the search bar rather than as a second
		    search UI in the sidebar. onFacets lets it refine this page in place. */}
		<EnquiryForm
			config={config.formConfig}
			type="green"
			hideLocation={false}
			onFacets={applyConciergeFacets}
			onEnquiry={applyEnquiry}
			onProximity={showProximity}
			currentPath={config.path}
			visibleProductIds={searchResults.products.map( product => product.id )}
		/>
		
		<div className="grid-x grid-padding-x">

			<div className="cell small-12 large-3">

				<Filters
					key={filtersKey}
					filterDefinitions={filterDefinitions}
					originalPriceRange={originalPriceRange}
					layout={layout}
					switchLayout={switchLayout}
					runFilters={runFilters}
				/>

				{config.advert ? (
					<div className="show-for-large mg-v-lrg">
						<Advert />
					</div>
				) : null}
				
			</div>

			<div className="cell small-12 large-9">
				<>
				{loading &&
					<div className="loader small-12 cell text-center">
						<img src={require(`loader-blocks.gif`)}/>
					</div>
				}
				{!loading &&

					<List
						layout={layout}
						config={config}
						results={searchResults}
						order={searchResults.order}
						changeOrder={changeOrder}
						showMap={showMap}
						toggleMap={() => setShowMap( !showMap )}
						compared={compared}
						near={payload.filters.near}
						clearNear={clearNear}
					/>
				}
				</>
			</div>
		</div>
		
	  </React.Fragment>
    );
}

const Loader = props => (
	<div className="loader small-12 cell text-center">
		<img src={require(`loader-blocks.gif`)}/>
	</div>
);

export default Grid
